'use client'

import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {BenefitsList} from '@/components/common/benefits/BenefitsList'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {HeroBenefitList} from '@/components/common/hero/HeroBenefitList'
import {TrademarkRegistrationHeroVideo} from '@/components/common/hero/video/TrademarkRegistrationHeroVideo'
import {HowItWorks} from '@/components/common/how-it-works/HowItWorks'
import {OfficeLocations} from '@/components/common/office-locations/OfficeLocations'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {LicensedAttorneys} from '@/components/common/team/LicensedAttorneys'
import {WhyTramaComparison} from '@/components/common/why-trama/comparison/WhyTramaComparison'
import {BrandProtectionCardList} from '@/components/homepage/cards/BrandProtectionCardList'
import {CountriesOffer} from '@/components/homepage/countries/CountriesOffer'
import {OurClientsAndStatistics} from '@/components/homepage/OurClientsAndStatistics'
import {TrademarkImportance} from '@/components/homepage/TrademarkImportance'
import {Page} from '@/components/Page'
import {Testimonial} from '@/components/testimonial/Testimonial'
import {getCountrySpecificContent} from '@/content/countrySpecific'
import {useTranslation} from '@/i18n'
import {HERO_PADDING_DESKTOP, HERO_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {Jurisdiction} from '@/utils/countries/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type CountryLandingPageProps = {
  jurisdiction: Jurisdiction
  numOfRegisteredTrademarks: number
  mostRecentArticles: RecentArticleCard[]
}

const CountryLandingPage: FC<CountryLandingPageProps> = ({
  jurisdiction,
  numOfRegisteredTrademarks,
  mostRecentArticles,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {hero, cards, countriesOffer, howItWorks, whyRegisterTm, whyTrama, getStartedBanner} =
    getCountrySpecificContent(jurisdiction, language)

  const jurisdictionName = alpha2ToName(jurisdiction, language).split(' ').join('')

  const isEnLanguage = language === Locale.en

  return (
    <>
      <Page>
        <Hero
          title={t(hero.title)}
          subtitle={t(hero.subtitle)}
          primaryButtonLink={hero.primaryButtonLink}
          primaryButtonText={t(hero.primaryButtonText)}
          primaryButtonTrackedEvent={`HeroMainCTAClicked - ${jurisdiction} LP`}
          secondaryButtonLink={hero.pricingButtonLink}
          secondaryButtonText={t(hero.pricingButtonText)}
          secondaryButtonTrackedEvent={`HeroPricingClicked - ${jurisdiction} LP`}
          benefits={<HeroBenefitList list={hero.list} />}
          alignItems={isEnLanguage ? 'center' : 'flex-end'}
        >
          {isEnLanguage ? (
            <TrademarkRegistrationHeroVideo
              trackedEventParameters={{section: 'Hero', page: 'Country-specific', jurisdiction}}
            />
          ) : hero.imageSrc && hero.imageAlt ? (
            <div className={cn('image', hero.isIceCream && 'ice-cream')}>
              <Image
                src={hero.imageSrc}
                alt={hero.imageAlt}
                sizes={
                  hero.isIceCream
                    ? `${MOBILE_SCREENSIZE} 150px, ${TABLET_SCREENSIZE} 250px, 395px`
                    : `${MOBILE_SCREENSIZE} 255px, ${TABLET_SCREENSIZE} 380px, 560px`
                }
                fill
                priority
                fetchPriority="high"
                style={{objectFit: 'contain'}}
              />
            </div>
          ) : null}
        </Hero>
        <OurClientsAndStatistics
          numOfRegisteredTrademarks={numOfRegisteredTrademarks}
          backgroundColor={theme.palette.background.default}
          backgroundOverlay={{color: theme.palette.background.butter, heightRatio: 0.5}}
          hasBoxShadow
        />
        <BrandProtectionCardList list={cards} country={jurisdiction} />
        <Section hasPaddingTop>
          <LicensedAttorneys />
        </Section>
        <CountriesOffer
          description={countriesOffer.description}
          country={jurisdiction}
          trackedEventParameters={{
            position: 'Countries offer - Country-specific page',
            country: jurisdiction,
          }}
        />
        <Section hasPaddingTop>
          <HowItWorks
            description={howItWorks.description && t(howItWorks.description)}
            title={t(howItWorks.title)}
            steps={howItWorks.steps}
          />
        </Section>
        <Section hasPaddingTop>
          {jurisdiction === Jurisdiction.UK ? (
            <BenefitsList
              title={t(whyTrama.title)}
              subtitle={t(whyTrama.subtitle)}
              benefits={whyTrama.benefits}
            />
          ) : (
            <WhyTramaComparison
              numOfRegisteredTrademarks={numOfRegisteredTrademarks}
              country={jurisdiction}
            />
          )}
        </Section>
        <TrademarkImportance benefits={whyRegisterTm.benefits} />
        <Testimonial />
        {jurisdiction === Jurisdiction.US && (
          <Section hasPaddingTop>
            <OfficeLocations />
          </Section>
        )}
        {mostRecentArticles.length > 0 && (
          <Section hasPaddingTop hasPaddingBottom>
            <MostRecentArticles
              articles={mostRecentArticles}
              trackedEvent={{
                name: `RecentBlogs-${jurisdictionName}`,
              }}
            />
          </Section>
        )}
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={getStartedBanner.buttonLink}
          primaryButtonText={t(getStartedBanner.buttonText)}
          primaryButtonTrackedEvent={`GetStartedBanner-${jurisdiction}`}
        />
      </Page>

      <style jsx>{`
        .list {
          margin-top: ${theme.spacing[3.5]};
        }
        .image {
          position: absolute;
          width: 560px;
          height: 350px;
          right: ${theme.spacing[2]};
          margin-bottom: -${HERO_PADDING_DESKTOP};
        }
        .ice-cream {
          width: 395px;
          height: 445px;
          margin-right: ${theme.spacing[5]};
          margin-top: -${theme.spacing[7]};
        }
        @media ${TABLET_SCREENSIZE} {
          .list {
            display: inline-flex;
            flex-direction: column;
          }
          .image {
            position: relative;
            right: 0;
            width: 380px;
            height: 230px;
            margin: ${theme.spacing[5]} auto -${HERO_PADDING_DESKTOP};
          }
          .ice-cream {
            width: 250px;
            height: 250px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 255px;
            height: 160px;
            margin: ${theme.spacing[5]} auto -${HERO_PADDING_MOBILE};
          }
          .ice-cream {
            width: 150px;
            height: 150px;
          }
        }
      `}</style>
    </>
  )
}

export default CountryLandingPage
